import { useState, useEffect, useCallback } from "react";
import { useAuth } from "./AuthContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  LayoutDashboard, Package, ClipboardList, FileText, Users, LogOut,
  DollarSign, Clock, AlertTriangle, Truck, Download, Menu, X, ChevronRight, BookOpen, Star, Bell,
  BarChart3, Mail, MessageSquare, UserPlus, Paintbrush, ExternalLink, Tag
} from "lucide-react";
import KanbanBoard from "./KanbanBoard";
import OrderDetail from "./OrderDetail";
import OrderList from "./OrderList";
import CreateOrder from "./CreateOrder";
import StaffManagement from "./StaffManagement";
import QuotesList from "./QuotesList";
import BlogManagement from "./BlogManagement";
import ReviewsList from "./ReviewsList";
import AnalyticsDashboard from "./AnalyticsDashboard";
import LeadManagement from "./LeadManagement";
import CampaignBuilder from "./CampaignBuilder";
import DesignsList from "./DesignsList";
import CouponManagement from "./CouponManagement";
import PricingAdmin from "./PricingAdmin";
import SMSDashboard from "./SMSDashboard";
import SEOAdmin from "./SEOAdmin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function BounceLog() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const fetchEntries = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/staff/bounce-log`, { withCredentials: true });
      setEntries(data.entries || []);
    } catch (err) {
      console.error("Failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/staff/bounce-log/send-now`, {}, { withCredentials: true });
      alert(data.message);
      fetchEntries();
    } catch (err) {
      alert("Failed to send digest");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="bounce-log">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
          <Bell className="w-5 h-5 sm:w-6 sm:h-6 text-orange-400" /> Bounce & Failure Log
        </h2>
        <Button onClick={sendNow} disabled={sending || entries.length === 0} className="bg-gradient-to-r from-orange-500 to-red-600 text-white w-full sm:w-auto" data-testid="send-digest-btn">
          {sending ? "Sending..." : `Send Digest Now (${entries.length})`}
        </Button>
      </div>
      <p className="text-zinc-500 text-sm">Email bounces, SMS failures, and opt-outs are queued here. A summary digest is sent daily at 6 PM PT.</p>
      {loading ? (
        <div className="text-zinc-500 text-center py-8">Loading...</div>
      ) : entries.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="py-12 text-center">
            <Bell className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400">No pending entries — all clear!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-zinc-500 border-b border-zinc-800">
                <th className="text-left py-2 px-3">Name</th>
                <th className="text-left py-2 px-3">Email</th>
                <th className="text-left py-2 px-3">Phone</th>
                <th className="text-left py-2 px-3">Type</th>
                <th className="text-left py-2 px-3">Reason</th>
                <th className="text-left py-2 px-3">Time</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={i} className="border-b border-zinc-800/50 text-zinc-300">
                  <td className="py-2 px-3">{e.contact_name || "—"}</td>
                  <td className="py-2 px-3">{e.email || "—"}</td>
                  <td className="py-2 px-3">{e.phone || "—"}</td>
                  <td className="py-2 px-3">
                    <Badge className={`text-xs ${e.source === 'email_bounce' ? 'bg-red-500/20 text-red-400' : e.source === 'sms_opt_out' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-orange-500/20 text-orange-400'}`}>
                      {(e.source || "").replace(/_/g, ' ')}
                    </Badge>
                  </td>
                  <td className="py-2 px-3 text-zinc-500">{e.reason || "—"}</td>
                  <td className="py-2 px-3 text-zinc-600 text-xs">{e.created_at ? new Date(e.created_at).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function OMSDashboard() {
  const { user, logout } = useAuth();
  const [view, setView] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [mobileNav, setMobileNav] = useState(false);

  const [navContext, setNavContext] = useState({});

  const navigateTo = (viewId, context = {}) => {
    setNavContext(context);
    setView(viewId);
    setMobileNav(false);
  };

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/oms/dashboard`, { withCredentials: true });
      setStats(data);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const openOrder = (id) => {
    setSelectedOrderId(id);
    setView("order-detail");
  };

  const handleExport = async () => {
    try {
      const resp = await axios.get(`${API}/oms/export/orders`, { withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orders_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
    }
  };

  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "analytics", label: "Analytics", icon: BarChart3 },
    { id: "kanban", label: "Kanban Board", icon: ClipboardList },
    { id: "orders", label: "Orders", icon: Package },
    { id: "new-order", label: "New Order", icon: FileText },
    { id: "leads", label: "Leads & Clients", icon: UserPlus },
    { id: "campaigns", label: "Campaigns", icon: Mail },
    { id: "quotes", label: "Quotes", icon: DollarSign },
    { id: "designs", label: "Designs", icon: Paintbrush },
    { id: "coupons", label: "Coupons", icon: Tag },
    { id: "pricing", label: "Pricing", icon: DollarSign },
    { id: "sms", label: "SMS", icon: MessageSquare },
    { id: "seo", label: "SEO / Domain", icon: ExternalLink },
    { id: "blog", label: "Blog", icon: BookOpen },
    { id: "reviews", label: "Reviews", icon: Star },
    { id: "bounce-log", label: "Bounce Log", icon: Bell },
    ...(user?.role === "admin" || user?.role === "super_admin" ? [{ id: "staff", label: "Staff", icon: Users }] : []),
  ];

  const renderView = () => {
    if (view === "order-detail" && selectedOrderId) {
      return <OrderDetail orderId={selectedOrderId} onBack={() => { setView("orders"); setSelectedOrderId(null); fetchStats(); }} />;
    }
    if (view === "kanban") return <KanbanBoard onOpenOrder={openOrder} />;
    if (view === "analytics") return <AnalyticsDashboard onNavigate={navigateTo} />;
    if (view === "leads") return <LeadManagement initialContext={navContext} />;
    if (view === "campaigns") return <CampaignBuilder />;
    if (view === "orders") return <OrderList onOpenOrder={openOrder} />;
    if (view === "new-order") return <CreateOrder onCreated={(o) => { openOrder(o.id); fetchStats(); }} />;
    if (view === "quotes") return <QuotesList />;
    if (view === "designs") return <DesignsList />;
    if (view === "coupons") return <CouponManagement />;
    if (view === "pricing") return <PricingAdmin />;
    if (view === "sms") return <SMSDashboard />;
    if (view === "seo") return <SEOAdmin />;
    if (view === "blog") return <BlogManagement />;
    if (view === "reviews") return <ReviewsList />;
    if (view === "bounce-log") return <BounceLog />;
    if (view === "staff") return <StaffManagement />;
    return renderDashboard();
  };

  const renderDashboard = () => {
    if (!stats) return <div className="flex items-center justify-center h-64 text-zinc-500">Loading...</div>;
    return (
      <div className="space-y-6" data-testid="oms-dashboard">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">Overview</h2>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExport} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-2" /> Export Orders
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.open(`${API}/crm/export/clients`, "_blank")} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="export-clients-btn">
              <Download className="w-4 h-4 mr-2" /> Export Clients
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.open(`${API}/crm/export/leads`, "_blank")} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="export-leads-btn">
              <Download className="w-4 h-4 mr-2" /> Export Leads
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Orders This Week" value={stats.orders_this_week} icon={Package} color="blue" />
          <StatCard label="In Production" value={stats.in_production} icon={Clock} color="amber" />
          <StatCard label="Overdue" value={stats.overdue} icon={AlertTriangle} color="red" />
          <StatCard label="Revenue (Paid)" value={`$${stats.total_revenue.toLocaleString()}`} icon={DollarSign} color="green" />
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Total Orders" value={stats.total_orders} icon={ClipboardList} color="zinc" />
          <StatCard label="On Hold" value={stats.on_hold} icon={AlertTriangle} color="orange" />
          <StatCard label="Due Soon" value={stats.due_soon} icon={Clock} color="yellow" />
          <StatCard label="Pending Revenue" value={`$${stats.pending_revenue.toLocaleString()}`} icon={DollarSign} color="purple" />
        </div>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader><CardTitle className="text-white text-lg">Pipeline</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-3">
              {Object.entries(stats.stage_counts).map(([stage, count]) => (
                <button
                  key={stage}
                  onClick={() => setView("kanban")}
                  className="text-center p-3 rounded-lg bg-zinc-800 hover:bg-zinc-750 transition cursor-pointer"
                >
                  <div className="text-2xl font-bold text-white">{count}</div>
                  <div className="text-xs text-zinc-400 capitalize">{stage.replace(/_/g, " ")}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex" data-testid="oms-layout">
      {/* Mobile nav overlay */}
      {mobileNav && <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setMobileNav(false)} />}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-zinc-900 border-r border-zinc-800 transform transition-transform lg:translate-x-0 flex flex-col ${mobileNav ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center justify-between p-4 border-b border-zinc-800 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-white font-bold text-sm leading-tight">Fit Print Bros</h1>
              <p className="text-zinc-500 text-xs">Order Management</p>
            </div>
          </div>
          <button className="lg:hidden text-zinc-400" onClick={() => setMobileNav(false)}><X className="w-5 h-5" /></button>
        </div>

        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = view === item.id;
            return (
              <button
                key={item.id}
                onClick={() => { setView(item.id); setMobileNav(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${active ? "bg-orange-500/10 text-orange-400" : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800"}`}
                data-testid={`nav-${item.id}`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="shrink-0 p-4 border-t border-zinc-800 bg-zinc-900">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center text-xs text-zinc-300 font-bold uppercase">
              {user?.name?.[0] || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white font-medium truncate">{user?.name}</p>
              <p className="text-xs text-zinc-500 truncate">{user?.role}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open("/", "_blank")}
              className="flex-1 border-zinc-700 text-zinc-300 hover:text-white hover:bg-zinc-800 text-xs"
              data-testid="back-to-site-btn"
            >
              <ExternalLink className="w-3 h-3 mr-1" />
              View Website
            </Button>
            <Button variant="ghost" size="sm" onClick={logout} className="text-zinc-400 hover:text-red-400 hover:bg-zinc-800" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-zinc-950/80 backdrop-blur-xl border-b border-zinc-800 px-4 py-3 flex items-center gap-3 lg:px-6">
          <button className="lg:hidden text-zinc-400" onClick={() => setMobileNav(true)}>
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 text-sm text-zinc-500">
            <span>OMS</span>
            <ChevronRight className="w-3 h-3" />
            <span className="text-zinc-300 capitalize">{view.replace(/-/g, " ")}</span>
          </div>
        </header>
        <div className="p-4 lg:p-6 max-w-7xl mx-auto">
          {renderView()}
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  const colorMap = {
    blue: "from-blue-500/20 to-blue-500/5 text-blue-400",
    amber: "from-amber-500/20 to-amber-500/5 text-amber-400",
    red: "from-red-500/20 to-red-500/5 text-red-400",
    green: "from-green-500/20 to-green-500/5 text-green-400",
    zinc: "from-zinc-700/40 to-zinc-700/10 text-zinc-300",
    orange: "from-orange-500/20 to-orange-500/5 text-orange-400",
    yellow: "from-yellow-500/20 to-yellow-500/5 text-yellow-400",
    purple: "from-purple-500/20 to-purple-500/5 text-purple-400",
  };
  const cls = colorMap[color] || colorMap.zinc;

  return (
    <div className={`bg-gradient-to-br ${cls} rounded-xl p-4 border border-zinc-800`}>
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-5 h-5 opacity-60" />
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
      <div className="text-xs opacity-60 mt-1">{label}</div>
    </div>
  );
}
